export type PIIType = 'name' | 'dob' | 'id' | 'district' | 'campus' | 'evaluator' | 'other'

export interface PIIEntry {
  placeholder: string
  type: PIIType
}

export interface StudentRegistry {
  token: string        // "S1", "S2"
  entries: PIIEntry[]
  grade: string        // "UNK" until inferred
  docType: string      // "DOC" until inferred
  realName: string
}

export interface RedactSession {
  registry: Map<string, StudentRegistry>   // keyed by name placeholder e.g. "[STUDENT_NAME]"
  nextIndex: number
}

export function createSession(): RedactSession {
  return { registry: new Map(), nextIndex: 1 }
}

// Regex: matches any [PLACEHOLDER] token in redacted text
const PLACEHOLDER_RE = /\[([A-Z_0-9]+)\]/g

// Infer type from placeholder name
function inferType(raw: string): PIIType {
  const r = raw.toUpperCase()
  if (r.includes('NAME')) return 'name'
  if (r.includes('DOB') || r.includes('DATE') || r.includes('BIRTH')) return 'dob'
  if (r.includes('ID') || r.includes('SSN')) return 'id'
  if (r.includes('DISTRICT')) return 'district'
  if (r.includes('CAMPUS') || r.includes('SCHOOL')) return 'campus'
  if (r.includes('EVAL') || r.includes('TEACHER') || r.includes('STAFF')) return 'evaluator'
  return 'other'
}

// Infer grade from redacted text — looks for patterns like "5th grade", "Grade 3", "3rd"
export function inferGrade(text: string): string {
  const m = text.match(/(\d+(?:st|nd|rd|th))\s*grade/i)
    || text.match(/grade\s*(\d+)/i)
    || text.match(/\b(\d+(?:st|nd|rd|th))\b/i)
  return m ? m[1].toLowerCase() : 'unk'
}

// Infer doc type from filename or text
export function inferDocType(filename: string, text: string): string {
  const combined = (filename + ' ' + text).toUpperCase()
  if (combined.includes('FIE') || combined.includes('FULL INDIVIDUAL')) return 'FIE'
  if (combined.includes('IEP')) return 'IEP'
  if (combined.includes('FVE') || combined.includes('FUNCTIONAL VISION')) return 'FVE'
  if (combined.includes('LMA') || combined.includes('LEARNING MEDIA')) return 'LMA'
  if (combined.includes('EVAL')) return 'EVAL'
  if (combined.includes('PROGRESS')) return 'PROGRESS'
  return 'DOC'
}

// Primary function — parse redacted text, assign tokens, return registry entry
export function registerDoc(
  session: RedactSession,
  redactedText: string,
  filename: string,
  realName: string = 'Unknown',
  detectedGrade: string = '',
  detectedDocType: string = ''
): StudentRegistry {
  const placeholders = [...redactedText.matchAll(PLACEHOLDER_RE)]
  const seen = new Set<string>()
  const entries: PIIEntry[] = []

  for (const match of placeholders) {
    const raw = match[1]           // e.g. "STUDENT_NAME"
    const full = match[0]          // e.g. "[STUDENT_NAME]"
    if (seen.has(full)) continue
    seen.add(full)
    entries.push({ placeholder: full, type: inferType(raw) })
  }

  // Find the primary name placeholder to use as registry key
  const nameEntry = entries.find(e => e.type === 'name')
  const registryKey = nameEntry?.placeholder ?? `__doc_${session.nextIndex}`

  // Return existing student if already seen this name placeholder
  if (session.registry.has(registryKey)) {
    return session.registry.get(registryKey)!
  }

  // New student
  const token = `S${session.nextIndex++}`
  const grade = detectedGrade || inferGrade(redactedText)
  const docType = detectedDocType || inferDocType(filename, redactedText)

  const student: StudentRegistry = { token, entries, grade, docType, realName }
  session.registry.set(registryKey, student)
  return student
}

// Generate filename: "S1-5th_FIE.pdf"
export function generateFilename(student: StudentRegistry, originalFilename: string): string {
  const ext = originalFilename.match(/\.[^/.]+$/)?.[0] ?? '.txt'
  return `${student.token}-${student.grade}_${student.docType}${ext}`
}

// Build manifest text — stays local, never uploaded
export function buildManifest(session: RedactSession): string {
  const lines = [
    'REDACT AI — LOCAL MANIFEST',
    'DO NOT UPLOAD THIS FILE TO ANY AI TOOL',
    'Keep this file to cross-reference anonymized student tokens.',
    '─'.repeat(48),
    '',
  ]
  session.registry.forEach((student) => {
    lines.push(`${student.token}`)
    lines.push(`  Student name    : ${student.realName}`)
    lines.push(`  Grade           : ${student.grade}`)
    lines.push(`  Doc type        : ${student.docType}`)
    lines.push(`  All placeholders: ${student.entries.map(e => e.placeholder).join(', ')}`)
    lines.push('')
  })
  return lines.join('\n')
}
